import { toSigFigs } from "../../../utils/format";
import { getCategoryDisplayName, isHiddenCategory } from "../../model/domain/definitions";

interface Props {
  emissions: Record<string, { Name: string; Value: number; Unit: string }[]>;
  analysisMode?: "ctg" | "ops";
}

const CATEGORY_ORDER = ["Air", "Soil", "Water", "Resources"];

export function PortfolioEmissionsCard({ emissions, analysisMode = "ctg" }: Props) {
  if (
    !emissions ||
    Object.values(emissions).every((rows) => rows.length === 0)
  ) {
    return null;
  }

  const renderTable = (
    rowsIn: { Name: string; Value: number; Unit: string }[],
  ) => {
    const rows = rowsIn.filter((r) => !isHiddenCategory(r.Name));
    return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-600">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
              Avoided Value
            </th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Unit
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr
              key={row.Name}
              className="hover:bg-surface-600 transition-colors"
            >
              <td className="px-4 py-2.5 text-sm text-text-secondary">
                {getCategoryDisplayName(row.Name)}
              </td>
              <td className="px-4 py-2.5 text-sm text-green-400/80 text-right font-medium whitespace-nowrap">
                {toSigFigs(Math.abs(row.Value))}
              </td>
              <td className="px-4 py-2.5 text-sm text-text-muted whitespace-nowrap">
                {row.Unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    );
  };

  return (
    <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h3 className="text-lg font-medium text-text-primary">
          Air, Soil, Water &amp; Resource Emissions
        </h3>
        <span className="text-xs text-text-muted bg-surface-600 px-2 py-0.5 rounded">
          {analysisMode === "ops" ? "Operations aggregate" : "Cradle-to-grave aggregate"}
        </span>
      </div>

      <div className="px-6 pb-5 space-y-5">
        {CATEGORY_ORDER.map((cat) => {
          const rows = emissions[cat];
          if (!rows || rows.length === 0) return null;
          return (
            <div key={cat}>
              <h4 className="text-sm font-medium text-text-primary mb-2">
                {cat}
              </h4>
              {renderTable(rows)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
